import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { API } from "../context/AppContext";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const AUDIENCES = [
  { key: "collectors", title: "For Collectors", features: ["Acquire Artwork", "Track Collections", "Manage Acquisition Records"] },
  { key: "artists", title: "For Artists", features: ["Inventory Management", "Artwork Cataloging", "Exhibition Tracking"] },
  { key: "curators", title: "For Curators", features: ["Manage Artists", "Manage Exhibitions", "Track Artwork Locations", "Manage Gallery Operations"] },
  { key: "galleries", title: "For Galleries", features: ["Artist Management", "Inventory Management", "Show Planning", "Sales Tracking"] },
];

const ROADMAP = [
  { phase: "Q3 2026", title: "Private Beta", body: "Artist inventory + artwork cataloging with founding artists." },
  { phase: "Q4 2026", title: "Collector Tools", body: "Collection tracking and acquisition records go live." },
  { phase: "Q1 2027", title: "Curator & Gallery Suite", body: "Exhibition planning, location tracking, and gallery operations." },
  { phase: "Q2 2027", title: "Public Launch", body: "Full platform availability with marketplace connections." },
];

function MockScreen({ label }) {
  return (
    <div className="border border-border bg-secondary/40 p-4 font-mono-x text-[10px]" data-testid={`datos-screen-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex gap-1.5 mb-4">
        <span className="w-2 h-2 bg-[#FF1A1A] rounded-full" />
        <span className="w-2 h-2 bg-[#B8860B] rounded-full" />
        <span className="w-2 h-2 bg-border rounded-full" />
      </div>
      <p className="uppercase tracking-widest text-[#FF1A1A] mb-3">{label}</p>
      <div className="space-y-2">
        {[80, 60, 90, 45, 70].map((w, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="h-2 bg-foreground/20" style={{ width: `${w}%` }} />
            <div className="h-2 w-6 bg-[#FF1A1A]/60 ml-auto" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="aspect-square bg-foreground/10 border border-border" />
        ))}
      </div>
    </div>
  );
}

export default function Datos() {
  const [form, setForm] = useState({ name: "", email: "", role: "Collector" });
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/waitlist`, form);
      setDone(true);
      toast.success("You're on the DATOS waitlist.");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
  };

  return (
    <main className="pt-24 sm:pt-32" data-testid="datos-page">
      <section className="container-x section-y relative">
        <div className="absolute top-24 right-5 sm:right-8 font-mono-x text-[10px] uppercase tracking-widest text-[#B8860B] border border-[#B8860B]/40 px-3 py-1.5" data-testid="datos-beta-badge">
          Private Beta — 2026
        </div>
        <p className="eyebrow mb-6">DATOS Gallery Pass — Art Technology</p>
        <MaskedLines lines={["Bridging Artists,", "Curators, Collectors,", "and Galleries"]} className="h-display max-w-5xl" />
        <Reveal delay={0.4}>
          <p className="mt-10 max-w-2xl text-muted-foreground" data-testid="datos-description">
            DATOS Gallery Pass is a platform that helps artists manage artwork inventories, curators organize exhibitions, collectors manage acquisitions, and galleries streamline operations.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="The Platform" title="One Pass. Four Audiences." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.key} delay={i * 0.06} className="bg-card">
                <div className="p-8 sm:p-10 h-full" data-testid={`datos-audience-${a.key}`}>
                  <h3 className="font-display font-extrabold uppercase text-xl mb-6">{a.title}</h3>
                  <ul className="space-y-3">
                    {a.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Check size={14} className="text-[#FF1A1A] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Product Preview" title="Inside the Pass" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Reveal><MockScreen label="Inventory" /></Reveal>
            <Reveal delay={0.08}><MockScreen label="Exhibitions" /></Reveal>
            <Reveal delay={0.16}><MockScreen label="Collections" /></Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Roadmap" title="Where DATOS Is Going" />
          <div className="relative pl-8 sm:pl-0" data-testid="datos-roadmap">
            <div className="absolute left-2 sm:left-0 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-10">
              {ROADMAP.map((r, i) => (
                <Reveal key={r.phase} delay={i * 0.06}>
                  <div className="relative sm:grid sm:grid-cols-12 sm:gap-8 items-baseline" data-testid={`roadmap-${r.phase.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="absolute -left-[26px] sm:left-[-5px] top-1.5 w-3 h-3 bg-[#FF1A1A] rotate-45" />
                    <p className="font-mono-x text-xs text-[#FF1A1A] sm:col-span-2 sm:pl-6">{r.phase}</p>
                    <div className="sm:col-span-10">
                      <h3 className="font-display font-extrabold uppercase text-lg">{r.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1 max-w-lg">{r.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-x section-y grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <p className="eyebrow mb-4">Early Access</p>
              <h2 className="h-section mb-4">Join the Waitlist</h2>
              <p className="text-muted-foreground max-w-md">Founding users shape the product. Tell us who you are and what you need DATOS to do.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            {done ? (
              <div className="card-line p-10 text-center" data-testid="datos-waitlist-success">
                <p className="font-display font-black uppercase text-2xl text-[#FF1A1A]">You're In</p>
                <p className="text-sm text-muted-foreground mt-3">We'll reach out when your cohort opens.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="card-line p-8 space-y-4" data-testid="datos-waitlist-form">
                <input className="input-x" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="datos-waitlist-name" aria-label="Full name" />
                <input className="input-x" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="datos-waitlist-input" aria-label="Email" />
                <select className="input-x" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="datos-waitlist-role" aria-label="Your role">
                  {["Collector", "Artist", "Curator", "Gallery"].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <button type="submit" className="btn-red w-full" data-testid="datos-waitlist-submit">Request Early Access</button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
